import { useMemo } from "react";

import type { BarbsIconTypeCustomOptions } from "@/constants/layerConfigAttributes";
import type { LayerKind } from "@/layers/utility/createLayerObject";
import { WindowSection } from "@/overlay/components";
import type { ParamOptionInputData } from "@/overlay/components/SmartForm/HookInputs/ParamOptionSelectList";
import { ToggleParametersGroup } from "@/overlay/components/SmartForm/HookInputs/ToggleParametersGroup";
import { WrappedWeatherParameterForm } from "@/overlay/components/SmartForm/HookInputs/WrappedWeatherParameterForm";
import { ModeSwitchableForm } from "@/overlay/components/SmartForm/SmartFormUtils/FormComponents/ModeSwitchableFormProps";
import { getUpdatedParamUnit } from "@/overlay/components/SmartForm/SmartFormUtils/FormDataFormatter/mergePartialWeatherParameter";
import { useFormWithUtils } from "@/overlay/components/SmartForm/SmartFormUtils/FormHooks/useFormUtils";
import { usePartialWeatherParameter } from "@/overlay/components/SmartForm/SmartFormUtils/FormHooks/usePartialWeatherParameter";
import type { BaseFormProps } from "@/overlay/components/SmartForm/SmartFormUtils/commonTypeUtils";
import { getBarbsLayerParameterSettings } from "@/utility/barbsLayerOptions";
import { getDefaultModelName } from "@/utility/layer";
import { getStandardSearchEngine } from "@/weather-parameters";
import type { BarbsLayer, BarbsLayerCreate } from "@mm/metx-workbench.meteomatics.com";
import type { HasLayerIdxNarrowing, PartialWeatherParameter } from "@mm/weather-parameter-utils";

type BarbsLayerPatch = Partial<BarbsLayerCreate | BarbsLayer>;

/**
 * Form data passed from the child components.
 */
export type BarbsLayerCreateFormData = {
  // Snake case as they get passed as layer props directly.
  layerData: {
    parameter_unit: string;
    parameter_unit_paired?: string;
    model: string;
    custom_options?: BarbsIconTypeCustomOptions & { icon_size?: number };
  };
  // Form internal data to construct the above data.
  internal: {
    defaultParameter: HasLayerIdxNarrowing<PartialWeatherParameter>;
    paramOptionPatch: ParamOptionInputData;
  };
};

type BarbsLayerCreateFormProps = {
  defaultFormData: BarbsLayerPatch;
} & BaseFormProps<BarbsLayerCreateFormData>;

/**
 * Responsible for rendering the whole layer form and send back the data on submit hadler.
 */
export function BarbsLayerCreateForm({ submitMode, onSubmit, onCancel, defaultFormData }: BarbsLayerCreateFormProps) {
  const { formMethods, formValName } = useFormWithUtils<BarbsLayerCreateFormData>({ mode: submitMode });
  const searchEngine = getStandardSearchEngine();

  const defaultParameterUnits = useMemo(() => {
    const standardSearchEngine = getStandardSearchEngine();
    if (!defaultFormData.parameter_unit) return [];
    if (defaultFormData.parameter_unit?.startsWith("wind_speed")) {
      return [
        standardSearchEngine.matchParameterNameExactly("wind_speed_10m:kn")[0],
        standardSearchEngine.matchParameterNameExactly("wind_gusts_10m_1h:kn")[0],
      ];
    }
    return [
      standardSearchEngine.matchParameterNameExactly("ocean_current_speed_10m:kn")[0],
      standardSearchEngine.matchParameterNameExactly("stokes_drift_speed:kn")[0],
      standardSearchEngine.matchParameterNameExactly("mean_direction_total_swell:d")[0],
      standardSearchEngine.matchParameterNameExactly("mean_wave_direction:d")[0],
    ];
  }, [defaultFormData.parameter_unit]);

  const formData = formMethods.watch();

  const { parameter } = usePartialWeatherParameter(
    defaultFormData.parameter_unit ?? "",
    defaultFormData.kind as LayerKind,
    searchEngine,
  );

  const onSubmitWithFormatting: typeof onSubmit = (formData) => {
    if (formData.internal?.paramOptionPatch.parameter?.available_models) {
      const selectedParameter = formData.internal.defaultParameter ?? parameter;
      const paramUnit = getUpdatedParamUnit(selectedParameter, [formData.internal.paramOptionPatch]);
      const barbsParameterSettings = getBarbsLayerParameterSettings(
        paramUnit,
        defaultFormData.custom_options as BarbsIconTypeCustomOptions,
      );

      const selectedModel = getDefaultModelName({ parameter: formData.internal.paramOptionPatch.parameter });
      onSubmit({
        ...formData,
        layerData: { parameter_unit: paramUnit, model: selectedModel, ...barbsParameterSettings },
      });
    }
  };

  if (!defaultFormData.kind) return <></>;

  return (
    <ModeSwitchableForm
      onSubmit={formMethods.handleSubmit(onSubmitWithFormatting)}
      onCancel={onCancel}
      submitMode={submitMode}
    >
      <WindowSection>
        <ToggleParametersGroup
          parameters={defaultParameterUnits}
          instruction={{
            formValueName: formValName("internal.defaultParameter"),
            currValue: parameter,
            formMethods,
          }}
        />
        <WrappedWeatherParameterForm
          instruction={{
            currValue: {
              kind: defaultFormData.kind as LayerKind,
              parameter: formData.internal?.defaultParameter || parameter,
            },
            formMethods,
            formValueName: formValName("internal.paramOptionPatch"),
          }}
        />
      </WindowSection>
    </ModeSwitchableForm>
  );
}
